import { derived, get, writable } from 'svelte/store';
import { activeCompanyId } from './company.js';
import { companiesApi } from './api.js';

/**
 * Cosa può fare l'utente nell'azienda attiva, detto dal server.
 *
 * I ruoli in stores/roles.js sono globali, mentre chi può gestire le
 * impostazioni email o inviare una copia di cortesia si decide azienda per
 * azienda: un amministratore globale che in questa azienda è operatore è un
 * operatore. Così l'interfaccia chiede una volta, invece di provare una
 * richiesta che verrebbe rifiutata.
 *
 * `null` finché la prima risposta non è arrivata, e di nuovo `null` subito
 * dopo un cambio di azienda: le capacità dell'azienda precedente non restano
 * mai in vista. Un aggiornamento per la stessa azienda (dopo aver salvato le
 * impostazioni email) tiene invece il valore corrente finché arriva il nuovo:
 * azzerarlo nasconderebbe per un attimo la scheda Email, che verrebbe
 * smontata e rimontata perdendo quello che mostra. Il server viene
 * interpellato solo quando qualche schermata le legge.
 */

const NONE = Object.freeze({
  role: null,
  canManageSmtp: false,
  canSendCourtesyEmail: false,
  smtpFeatureAvailable: false,
  smtpConfigured: false,
});

const refreshTick = writable(0);
let shownFor = null;
/** L'ultima risposta arrivata, con l'azienda a cui si riferisce (per le guardie di rotta). */
let latest = null;

export const capabilities = derived([activeCompanyId, refreshTick], ([$companyId], set) => {
  if ($companyId !== shownFor) {
    shownFor = $companyId;
    set(null);
  }
  if (!$companyId) return undefined;
  let current = true;
  companiesApi.capabilities($companyId)
    .then((value) => { if (current) { latest = { companyId: $companyId, value }; set(value); } })
    .catch(() => { if (current) set(NONE); });
  return () => { current = false; };
}, null);

/** Da chiamare dopo aver cambiato le impostazioni email. */
export function refreshCapabilities() {
  refreshTick.update((n) => n + 1);
}

/**
 * I ruoli aziendali che possono scrivere: la stessa regola che il server
 * applica a ogni POST/PUT/PATCH/DELETE in `companyContext()`. Conta il ruolo
 * nell'azienda (un amministratore globale che non ne è membro vale come
 * amministratore), non il ruolo globale: un operatore dell'azienda con un
 * ruolo globale di sola consultazione scrive, un amministratore globale che
 * nell'azienda è in sola consultazione no.
 */
export const WRITE_ROLES = Object.freeze(['admin', 'operator']);

export function isWriteRole(role) {
  return WRITE_ROLES.includes(role);
}

/**
 * Se l'utente può modificare dati nell'azienda attiva — fatture, bozze,
 * pagamenti, anagrafiche, metodi di pagamento. È l'unica regola per tutte le
 * azioni di scrittura dell'interfaccia. Falso finché le capacità non sono
 * arrivate: meglio un pulsante che compare un attimo dopo che uno che non
 * funziona.
 */
export const canWriteInCompany = derived(capabilities, ($capabilities) => isWriteRole($capabilities?.role));

/**
 * Il ruolo nell'azienda attiva, aspettando la risposta del server fino a
 * `timeoutMs`. Per le guardie di rotta, che girano prima che una pagina si
 * iscriva alle capacità. `undefined` se non arriva in tempo o non c'è
 * un'azienda attiva.
 */
export function companyRole(timeoutMs = 4000) {
  const companyId = get(activeCompanyId);
  if (!companyId) return Promise.resolve(undefined);
  if (latest?.companyId === companyId) return Promise.resolve(latest.value?.role ?? undefined);
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    const timer = setTimeout(() => { unsubscribe(); resolve(undefined); }, timeoutMs);
    unsubscribe = capabilities.subscribe((value) => {
      if (value === null) return;
      clearTimeout(timer);
      queueMicrotask(() => unsubscribe());
      resolve(value?.role ?? undefined);
    });
  });
}
